import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getMeetingAttributionAudioUrl } from '@/api/internal'
import { attributionViewerKeys } from '@/pages/app/AttributionViewer/queryKeys'
import { useAudioClipPlayer } from '@/pages/app/AttributionViewer/useAudioClipPlayer'

const AUDIO_MINT_LIFETIME_MS = 12 * 60 * 1000

export type GtAudioState = 'idle' | 'loading' | 'present' | 'absent' | 'error'

export const useGtAudio = (meetingId: string, enabled: boolean) => {
  const player = useAudioClipPlayer()
  const playerRef = useRef(player)
  playerRef.current = player
  const [ready, setReady] = useState(false)
  const [durationMs, setDurationMs] = useState<number | null>(null)
  const [mediaFailed, setMediaFailed] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const mintedAtRef = useRef<number | null>(null)
  const pendingClipRef = useRef<{ startMs: number; endMs?: number; play: boolean } | null>(null)
  const mediaRetryRef = useRef(0)

  const audioQuery = useQuery({
    enabled: Boolean(meetingId) && enabled,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    queryKey: attributionViewerKeys.audio(meetingId),
    queryFn: ({ signal }) => getMeetingAttributionAudioUrl(meetingId, signal).then((response) => response.data),
  })

  const audioUrl = audioQuery.data?.url ?? null
  const { refetch: refetchAudio, dataUpdatedAt } = audioQuery

  // Keyed on dataUpdatedAt (not audioUrl) so a byte-identical re-minted URL still resets the clock.
  useEffect(() => {
    if (audioUrl) mintedAtRef.current = Date.now()
  }, [audioUrl, dataUpdatedAt])

  useEffect(() => {
    setReady(false)
    setDurationMs(null)
    setMediaFailed(false)
  }, [audioUrl])

  const onLoadedMetadata = useCallback((event: React.SyntheticEvent<HTMLAudioElement>) => {
    const seconds = event.currentTarget.duration
    setDurationMs(Number.isFinite(seconds) ? Math.round(seconds * 1000) : null)
    setReady(true)
    setMediaFailed(false)
    mediaRetryRef.current = 0
  }, [])

  // First failure re-mints the URL and resumes; second failure is a hard error, surfaced only via manual retry().
  const onError = useCallback(() => {
    setReady(false)
    if (mediaRetryRef.current > 0) {
      setMediaFailed(true)
      return
    }
    mediaRetryRef.current += 1
    pendingClipRef.current = pendingClipRef.current ?? {
      startMs: Math.round(playerRef.current.audioTime ?? 0),
      play: true,
    }
    void refetchAudio().then((result) => {
      if (!result.data?.url) {
        setMediaFailed(true)
        return
      }
      setMediaFailed(false)
      setLoadAttempt((attempt) => attempt + 1)
    })
  }, [refetchAudio])

  const retry = useCallback(() => {
    mediaRetryRef.current = 0
    setMediaFailed(false)
    void refetchAudio().then((result) => {
      if (!result.data?.url) return
      setLoadAttempt((attempt) => attempt + 1)
    })
  }, [refetchAudio])

  const isMintExpired = useCallback(
    () => mintedAtRef.current == null || Date.now() - mintedAtRef.current > AUDIO_MINT_LIFETIME_MS,
    []
  )

  const remintingRef = useRef(false)
  const remintThen = useCallback(
    (clip: { startMs: number; endMs?: number; play: boolean }) => {
      pendingClipRef.current = clip
      if (remintingRef.current) return
      remintingRef.current = true
      void refetchAudio()
        .then((result) => {
          if (result.data?.url) setLoadAttempt((attempt) => attempt + 1)
        })
        .finally(() => {
          remintingRef.current = false
        })
    },
    [refetchAudio]
  )

  const playFrom = useCallback(
    (startMs: number, endMs?: number) => {
      if (!isMintExpired()) {
        playerRef.current.playFrom(startMs, endMs)
        return
      }
      remintThen({ startMs, endMs, play: true })
    },
    [remintThen, isMintExpired]
  )

  // A seek into an unbuffered range fetches with the presigned URL, so an expired one needs a re-mint first.
  const seekTo = useCallback(
    (ms: number) => {
      if (!isMintExpired()) {
        playerRef.current.seekTo(ms)
        return
      }
      remintThen({ startMs: ms, play: false })
    },
    [remintThen, isMintExpired]
  )

  useEffect(() => {
    if (!ready || !pendingClipRef.current) return
    const clip = pendingClipRef.current
    pendingClipRef.current = null
    if (clip.play) playerRef.current.playFrom(clip.startMs, clip.endMs)
    else playerRef.current.seekTo(clip.startMs)
  }, [ready, loadAttempt])

  const state: GtAudioState = !enabled
    ? 'idle'
    : audioQuery.isError || mediaFailed
      ? 'error'
      : audioQuery.isPending || (audioUrl != null && !ready)
        ? 'loading'
        : audioUrl == null
          ? 'absent'
          : 'present'

  return useMemo(
    () => ({
      ...player,
      audioUrl,
      state,
      ready,
      durationMs,
      loadAttempt,
      onLoadedMetadata,
      onError,
      retry,
      playFrom,
      seekTo,
    }),
    [player, audioUrl, state, ready, durationMs, loadAttempt, onLoadedMetadata, onError, retry, playFrom, seekTo]
  )
}
